import AsyncStorage from '@react-native-async-storage/async-storage';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const STORAGE_KEY = 'tabCardBannerDismissal';

const COOLDOWN_DAYS = [14, 60];

interface DismissalState {
  dismissCount: number;
  showAt: string;
}

interface UseTabCardBannerResult {
  dismissBanner: () => Promise<number>;
  isDismissed: boolean;
}

export const useTabCardBanner = (
  restaurantUUID: string,
): UseTabCardBannerResult => {
  const combinedStorageKey = `${STORAGE_KEY}.${restaurantUUID}`;

  // Start dismissed (hidden) until async check confirms otherwise
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(combinedStorageKey);

        if (!raw) {
          setIsDismissed(false);
        } else {
          const state: DismissalState = JSON.parse(raw);
          const showAt = moment(state.showAt);
          setIsDismissed(moment().isBefore(showAt));
        }
      } catch {
        // Clear corrupted data so future reads can start fresh
        await AsyncStorage.removeItem(combinedStorageKey).catch(() => {});
        setIsDismissed(false);
      }
    })();
  }, [combinedStorageKey]);

  const dismissBanner = useCallback(async (): Promise<number> => {
    try {
      const raw = await AsyncStorage.getItem(combinedStorageKey);
      const current: DismissalState = raw
        ? JSON.parse(raw)
        : { dismissCount: 0, showAt: '' };

      const newCount = current.dismissCount + 1;
      const cooldownDays =
        newCount <= COOLDOWN_DAYS.length
          ? COOLDOWN_DAYS[newCount - 1]
          : undefined;

      const showAt = cooldownDays
        ? moment().startOf('day').add(cooldownDays, 'days').toISOString()
        : moment('9999-12-31').toISOString();

      const newState: DismissalState = {
        dismissCount: newCount,
        showAt,
      };

      await AsyncStorage.setItem(combinedStorageKey, JSON.stringify(newState));
      setIsDismissed(true);

      return newCount;
    } catch {
      // Clear corrupted data so future dismissals can start fresh
      await AsyncStorage.removeItem(combinedStorageKey).catch(() => {});
      return 0;
    }
  }, [combinedStorageKey]);

  return useMemo(
    () => ({
      dismissBanner,
      isDismissed,
    }),
    [dismissBanner, isDismissed],
  );
};
